export type APP = 'QUICKSIGNER' | 'ESEMNEAZA';

export type ENV = 'LOCAL' | 'DEV' | 'PROD';

export type Language = 'ro' | 'en';

export type Feature =
    | 'SUPER'
    | 'ADMIN'
    | 'API'
    | 'PHONE_RECIPIENTS'
    | 'ONE_CLICK_SIGN'
    | 'ADDITIONAL_FILES'
    | 'NO_BRANDING_SIGNING'
    | 'IMAGE_ONLY_SIGNATURE'
    | 'EXTRACT_TAGS'
    | 'EXTRACT_ACRO_FORM'
    | 'API_DEV'
    | 'CUSTOM_BRANDING'
    | 'USAGE_TRACKING'
    | 'ACCOUNT_ADMIN_VIEW_PERMISSION';

export type Features = {
    [key in Feature]?: boolean;
};

export type CustomerType =
    | 'NEW'
    | 'ACTIVE_SUBSCRIPTION'
    | 'ACTIVE_SUBSCRIPTION_PAID_OFFLINE'
    | 'CANCELED_ACTIVE_SUBSCRIPTION'
    | 'INACTIVE';

export type Plan = 'PAID' | 'FREE' | 'PRO';

export interface User {
    id: string;
    name: string;
    email: string;
    allowedRecipientsNum: number;
    emailVerified: boolean;
    lastEmailVerificationSentAt?: string | null;
    plan: Plan;
    signatureUrl: string | null;
    features: Features;
    paidUntil: string;
    customerType: CustomerType;
    maxDocumentSize: number;
    maxDocuments: number;
    type: RecipientType;
    numOfSeats: number | null;
    isDrawSigEnabled: boolean;
    isTextSigEnabled: boolean;
    isUploadSigEnabled: boolean;
    packages?: Package[];
    // freeDaysLeft?: number;
    brand: Brand | null;
    appSumoTier?: number;
    disableNotifs?: boolean | null;
    disableConsent?: boolean | null;
    isTeamMember: boolean;
    signatureBase64?: string | null;
}

export interface ApiKeyData {
    id: string;
    type: string;
    name: string;
    lastChars: string;
    createdAt: string;
}

export enum DnDTypes {
    SOURCE_FIELD = 'SOURCE_FIELD',
    FIELD = 'FIELD',
    DOCUMENT = 'DOCUMENT',
}

export enum FieldTypes {
    SIGNATURE = 'SIGNATURE',
    TEXT = 'TEXT',
    STAMP = 'STAMP',
    CHECKBOX = 'CHECKBOX',
    FILE = 'FILE',
    TEXTAREA = 'TEXTAREA',
    DATE = 'DATE',
    INITIALS = 'INITIALS',
}

export enum FieldMode {
    SIGNATURE = 'SIGNATURE',
    FIELD = 'FIELD',
    PREFILL = 'PREFILL',
}

export interface Field {
    id: number | string;
    type: FieldTypes;
    y: number;
    x: number;
    width: number;
    height: number;
    rawDimensions?: boolean;
    pageNum: number;
    // 0 = main document, 1..N = additional documents
    documentIndex?: number;
    isSelected: boolean;
    recipientIndex: number | null;
    description: string | null;
    mode: FieldMode;
    signature?: string | null;
    source?: 'api' | 'user';
    sigType?: 'image' | 'text' | 'file' | 'textarea';
    // isFirstRender?: boolean;
    required?: boolean;
    fixed?: boolean;
    groupId?: number | null;
    format?: string | null;
    isRadio?: boolean;
}

export interface RecipientTag {
    index: number;
    email: string;
    type: RecipientType;
}

export interface SignData {
    docUrl: string;
    fields: BackendField[];
    documents?: RequestDocumentInfo[];
    senderName: string;
    senderEmail: string;
    isSenderVerfied: boolean;
    isApiRequest: boolean;
    // ipAddress: string;
    signerEmail: string;
    docName: string;
    recipientId: string;
    recipientIdFull: string;
    recipientName?: string | null;
    oneClickSign?: boolean | null;
    signedRedirectUrl?: string | null;
    declinedRedirectUrl?: string | null;
    recipientUserType: RecipientType;
    disableTextSignature?: boolean;
    noBrandingSigning?: boolean;
    hideInfoPanel?: boolean;
    englishLanguage?: boolean;
    lastSigUrl?: string | null;
    isDrawSigEnabled: boolean;
    isTextSigEnabled: boolean;
    isUploadSigEnabled: boolean;
    brand?: Brand | null;
    isOtpEnabled: boolean | null;
    otpMobileNumber?: string | null;
    otpChannel?: 'SMS' | 'WHATSAPP' | null;
    consentMessage?: string;
    sessionId?: string;
    sessionExpiresAt?: string;
}

type SmsStatus = 'SENDING' | 'ERROR' | 'SENT';

export interface Recipient {
    id: string;
    requestId: string;
    userId: string;
    order: number;
    sigStatus: `${SigStatus}`;
    turnStatus: `${TurnStatus}`;
    // user: User;
    fields: BackendField[];
    signedAt: string;
    name?: string | null;
    oneClickSign?: boolean | null;
    smsStatus?: SmsStatus;
    canSendReminder?: boolean;
    type: RecipientType;
    email?: string;
    phone?: string;
    signatures?: BackendField[];
    signLink?: string;
    rejectionReason?: string;
    isOtpEnabled?: boolean | null;
    otpMobileNumber?: string | null;
    otpVerification?: {
        isVerified: boolean | null;
        phoneNumber: string | null;
    };
    hasSeenRequest?: boolean | null;
}

export enum RecipientStatus {
    SIGNED = 'SIGNED',
    PENDING = 'PENDING',
    NEXT = 'NEXT',
    WAITING = 'WAITING',
    SIG_RECEIVED = 'SIG_RECEIVED',
    SMS_ERROR = 'SMS_ERROR',
    REJECTED = 'REJECTED',
}

export interface BackendField {
    id: string;
    type: FieldTypes;
    pageNum: number;
    documentIndex?: number;
    documentId?: string | null;
    recipientId: string;
    x: number;
    y: number;
    width: number;
    height: number;
    description: string | null;
    fixed?: boolean;
    url?: string;
}

// Keyed by pageKey(documentIndex, pageNum)
export interface PdfDimensions {
    [pageKey: string]: {
        widthRatio: number;
        heightRatio: number;
        width: number;
        height: number;
    };
}

export interface FrontEndSignature {
    id?: string;
    url?: string;
    width: number;
    height: number;
    type: FieldTypes;
    pageNum: number;
    documentIndex: number;
    x: number;
    y: number;
    base64?: string;
}

export enum RequestStatus {
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    PROCESSING = 'PROCESSING',
    CANCELED = 'CANCELED',
    TEMPLATE = 'TEMPLATE',
    PREFILLING = 'PREFILLING',
    REJECTED = 'REJECTED',
}

export interface BackedEndPrefillText {
    id: string;
    text: string;
    height: number;
    width: number;
    pageNum: number;
    x: number;
    y: number;
}

export interface AdditionalFile {
    fileUrl: string;
    id: string;
}

export interface Request {
    id: string;
    createdAt: string;
    senderId: string;
    senderName: string;
    docUrl?: string;
    docName: string;
    signInOrder: boolean;
    message: string;
    emailSubject?: string | null;
    status: `${RequestStatus}`;
    recipients?: Recipient[];
    sender?: User;
    canSign?: boolean;
    completedAt: string | null;
    tags: string[];
    preFillTexts?: BackedEndPrefillText[];
    files?: AdditionalFile[];
    copyOnlyRecipients?: { id: number; email: string }[];
    hideFromTeam?: boolean;
    downloadedAt?: string | null;
    // Additional documents (the main document is docUrl/docName)
    documents?: { id: string; docName: string; docUrl?: string }[];
}

export const RequestStatusRo = {
    [RequestStatus.COMPLETED]: 'FINALIZAT',
    [RequestStatus.IN_PROGRESS]: 'IN ASTEPTARE',
};

export interface AllDataRequest extends Request {}

export enum SigStatus {
    PENDING = 'PENDING',
    RECEIVED = 'RECEIVED',
    APPLIED = 'APPLIED',
    REJECTED = 'REJECTED',
}

export enum TurnStatus {
    WAITING = 'WAITING',
    NEXT = 'NEXT',
    COMPLETED = 'COMPLETED',
}

export const SigToRequestStatus = {
    [SigStatus.PENDING]: RequestStatus.IN_PROGRESS,
    [SigStatus.RECEIVED]: RequestStatus.IN_PROGRESS,
    [SigStatus.APPLIED]: RequestStatus.COMPLETED,
    [SigStatus.REJECTED]: RequestStatus.REJECTED,
};

export interface Dimensions {
    width: number;
    height: number;
}
export interface InitialFieldSizes {
    [FieldTypes.SIGNATURE]: Dimensions;
    [FieldTypes.TEXT]: Dimensions;
    [FieldTypes.STAMP]: Dimensions;
}

export enum Pages {
    Login = 'Login',
    Signup = 'Signup',
    ResetPassword = 'ResetPassword',
    Home = 'Home',
    EditFields = 'EditFields',
    CreateRequest = 'CreateRequest',
    SentRequests = 'SentRequests',
    ReceivedRequests = 'ReceivedRequests',
    Account = 'Account',
    Sign = 'Sign',
    SignError = 'SignError',
    SignSuccess = 'SignSuccess',
    Super = 'Super',
    Templates = 'Templates',
    CreateTemplate = 'CreateTemplate',
    EditTemplate = 'EditTemplate',
    CreateFromTemplate = 'CreateFromTemplate',
    PreFillDocument = 'PreFillDocument',
    Api = 'Api',
    BulkSent = 'BulkSent',
    BulkSendCreate = 'BulkSendCreate',
    EmailVerification = 'EmailVerification',
    EmailChangeConfirmation = 'EmailChangeConfirmation',
    CompletedDocument = 'CompletedDocument',
    Reports = 'Reports',
}

export enum SignErrorCodes {
    NotFound = 'not-found',
    InvalidSigner = 'invalid-signer',
    RequestCanceled = 'request-canceled',
    RequestCompleted = 'request-completed',
    AlreadySigned = 'already-signed',
    SignerNotNext = 'signer-not-next',
    Declined = 'request-declined',
    EmailNotVerified = 'email-not-verified',
    LinkExpired = 'link-expired',
}

export interface Document {
    name: string;
    path: string;
    // downloadUrl: string;
}

export interface AdditionalDocument extends Document {
    id: string;
    // Documents of the template a request is created from can't be removed
    isLocked?: boolean;
}

// Documents of an existing request; the main document has id null
export interface RequestDocumentInfo {
    index: number;
    id: string | null;
    docName: string;
}
export type RequestType = 'sent' | 'received' | 'template';

export type CreateRequestTypes =
    | 'newRequest'
    | 'newFromTemplate'
    | 'newTemplate'
    | 'editTemplate';

export type SubscriptionType = 'monthly' | 'annual';

export interface Package {
    id: string;
    type: 'API' | 'ENTERPRISE';
    startDate: Date | string;
    endDate: Date | string;
    numOfRequests: number;
}

export const EventType = {
    RECIPIENT_SIGNED: 'RECIPIENT_SIGNED',
    RECIPIENT_ERROR: 'RECIPIENT_ERROR',
};

export interface Webhook {
    id: string;
    userId?: string;
    url: string;
    events: string[];
    headerKey?: string;
    headerValue?: string;
}

export type RecipientType =
    | 'TEMPLATE_PLACEHOLDER'
    | 'EMAIL'
    | 'PHONE'
    | 'SIGN_LINK_ONLY';

export interface RecipientSettings {
    oneClickSign?: boolean;
    isCopyOnly?: boolean;
    englishLanguage?: boolean;
    signLinkOnly?: boolean;
    sendBySMS?: boolean;
    otpVerification?: boolean;
}

export type RecipientSettingsKeys = keyof RecipientSettings;

export type BulkRequestStatus = 'CREATED' | 'SENDING' | 'SENT';

export interface BulkRequest {
    id: string;
    createdAt: string;
    docName: string;
    docUrl: string;
    status: BulkRequestStatus;
    totalRequestsCount: number;
    sentRequestsCount: number;
    tags: any[];
    recipients: any[];
}

export interface AcroField {
    id: string;
    value: string;
    defaultValue: string;
    multiline: boolean;
    password: boolean;
    charLimit: number;
    comb: boolean;
    editable: boolean;
    hidden: boolean;
    name: string;
    rect: number[];
    actions: any;
    page: number;
    strokeColor: any;
    fillColor: any;
    rotation: number;
    type: string;
}

export type RequestSource = 'WEB' | 'BULK' | 'API' | 'API_DEV';

export type SeatStatus = 'INVITED' | 'COMPLETED';

export interface Seat {
    id: string;
    email: string;
    name: string;
    userId: string;
    status: SeatStatus;
    hasAdminViewPermissions: boolean;
}

export interface Team {
    id?: string;
    name: string;
    users: {
        id: string;
        email: string;
    }[];
    notifyOnNewRequest?: boolean;
}

export interface Brand {
    id?: string;
    logo?: string;
    logoWhite?: string | null;
    slug: string;
    color: string;
    accent: string;
    enabled: boolean;
    base64Logo?: string;
    base64LogoWhite?: string;
}

export interface AppSubsCount {
    week: number;
    year: number;
    business: number;
    pro: number;
    trial: number;
    offline: number;
}

export interface MonthlyAppSubsCount {
    month: number;
    year: number;
    business: number;
    pro: number;
    trial: number;
    monthKey: string;
}

export type EmailTextTemplateType = 'SUBJECT' | 'MESSAGE';

export interface EmailTextTemplate {
    id: string;
    content: string;
    type: EmailTextTemplateType;
    shared: boolean;
    ownerId: string;
    owner: {
        id: string;
        name: string | null;
        email: string;
    };
    createdAt: string;
}
